/* eslint-disable max-lines */
import { createContext, useState, useEffect } from 'react'

import { toastEventDispatch } from '@space-lib-events/toast'
import { ToastPosition, ToastType } from '@space-lib-events/toast/enum'
import type { ToggleDeleteModalEvent } from '@space-lib-events/toggleDeleteModal'
import {
  toggleDeleteModalEventDispatch,
  toggleDeleteModalEventListener
} from '@space-lib-events/toggleDeleteModal'
import {
  Actions,
  Entities,
  SurveyNames,
  EventNames,
  RequestResults,
  ScreenNames
} from '@space-lib/datahub'
import useMembership from '@space-shell/common/hooks/useMembership'
import { TOAST_EVENT_KEY } from '@space-shell/core/store'
import { lazyRemoteModule } from '@space-shell/lib/runtime'
import { PF_SURVEY_WHY_REMOVING_COMMUNITY } from '@space-shell/modules/communities/constants'
import useSendDataHubConsume from '@space-shell/modules/communities/hooks/useSendDataHubConsume/useSendDataHubConsume'

import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import {
  CONCLUDED_PROGRESS_BAR_VALUE,
  HALF_CONCLUDED_PROGRESS_BAR_VALUE,
  INITIAL_PROGRESS_BAR_VALUE,
  SURVEY_STEPS
} from './constants'
import type { DeleteCommunityModalContextProps, DeleteCommunityModalProviderProps } from './types'

import DeleteCommunityModal from '../..'
import ExpectedSuccessStep from '../../expectedSuccessStep'
import {
  EXPECTED_SUCCESS_ANSWER_DATAHUB_VALUES,
  EXPECTED_SUCCESS_VALUES,
  EXPECTED_SUCESS_QUESTION_DATAHUB_LABEL
} from '../../expectedSuccessStep/constants'
import { useDeleteCommunity } from '../../hooks/useDeleteCommunity'
import InitialExpectationStep from '../../initialExpectaitonStep'
import { INITIAL_EXPECTATION_QUESTION_DATAHUB_LABEL } from '../../initialExpectaitonStep/constants'
import MotivationStep from '../../motivationStep'
import {
  MOTIVATION_ANSWER_DATAHUB_VALUES,
  MOTIVATION_QUESTION_DATAHUB_LABEL
} from '../../motivationStep/constants'
import { MOTIVATION_VALUES } from '../../motivationStep/constants'

export const DeleteCommunityModalContext = createContext<DeleteCommunityModalContextProps>(
  {} as DeleteCommunityModalContextProps
)

export const DeleteCommunityModalProvider = ({ children }: DeleteCommunityModalProviderProps) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { membershipSlug, membershipData } = useMembership()
  const [isOpen, setIsOpen] = useState(false)

  const { sendDataHubConsume } = useSendDataHubConsume()
  const [communityId, setCommunityId] = useState<number>()
  const [communityName, setCommunityName] = useState<string>()
  const [motivationText, setMotivationText] = useState('')
  const [expectedSuccessText, setExpectedSuccessText] = useState('')
  const { deleteCommunityQuery, invalidateQueries } = useDeleteCommunity()
  const [initialExpectationText, setInitialExpectationText] = useState('')
  const [progressBarValue, setProgressBarValue] = useState(INITIAL_PROGRESS_BAR_VALUE)
  const [currentStep, setCurrentStep] = useState<SURVEY_STEPS>(SURVEY_STEPS.MOTIVATION)
  const [motivationSelectedOption, setMotivationSelectedOption] = useState<MOTIVATION_VALUES>()
  const [expectedSuccesSelectedOption, setExpectedSuccesSelectedOption] =
    useState<EXPECTED_SUCCESS_VALUES>()
  const {
    t,
    i18n: { language }
  } = useTranslation()

  const handleClose = () => {
    toggleDeleteModalEventDispatch({ action: 'close' })

    setMotivationText('')
    setExpectedSuccessText('')
    setInitialExpectationText('')
    setMotivationSelectedOption(undefined)
    setCurrentStep(SURVEY_STEPS.MOTIVATION)
    setExpectedSuccesSelectedOption(undefined)
    setProgressBarValue(INITIAL_PROGRESS_BAR_VALUE)
  }

  const handleDeleteCommunity = async (userResponse) => {
    const remoteModule = await lazyRemoteModule<
      () => { productFruitsApi: { surveys: { startSurvey: (arg: string) => void } } }
    >('community/ProductFruitsContext')

    const productFruitsApi = remoteModule?.().productFruitsApi

    if (productFruitsApi) {
      productFruitsApi.surveys?.startSurvey(PF_SURVEY_WHY_REMOVING_COMMUNITY)
    }

    deleteCommunityQuery.mutate(communityId as number, {
      onSuccess() {
        const toastDetail = {
          type: ToastType.SUCCESS,
          message: t('modal.delete_community.success'),
          position: ToastPosition.BOTTOM_RIGHT
        }

        sendDataHubConsume({
          entity: Entities.COMMUNITY,
          action: Actions.DELETE,
          event: {
            eventName: EventNames.DELETE_COMMUNITY,
            screenName: ScreenNames.COMMUNITY_VIEW,
            communityId
          }
        })

        sendDataHubConsume({
          entity: Entities.SURVEY_COMMUNITY_DELETE,
          action: Actions.ANSWER,
          event: {
            eventName: EventNames.COMMUNITY_DELETE_SURVEY,
            screenName: ScreenNames.COMMUNITY_FEED,
            communityId,
            communityName,
            membershipId: membershipData?.id,
            surveyName: SurveyNames.DELETE_COMMUNITY,
            ...userResponse
          }
        })

        invalidateQueries()
        handleClose()

        if (location.pathname.endsWith(`/club/${membershipSlug}`)) {
          toastEventDispatch(toastDetail)
        } else {
          window.localStorage.setItem(TOAST_EVENT_KEY, JSON.stringify(toastDetail))

          navigate(`/${language}/club/${membershipSlug}`)
        }
      },
      onError(error) {
        sendDataHubConsume({
          entity: Entities.COMMUNITY,
          action: Actions.DELETE,
          event: {
            eventName: EventNames.DELETE_COMMUNITY,
            screenName: ScreenNames.COMMUNITY_VIEW,
            result: RequestResults.FAILURE,
            failureKind: error?.message,
            communityId
          }
        })
      }
    })
  }

  const isCurrentStepMotivation = currentStep === SURVEY_STEPS.MOTIVATION
  const isMotivationTextEmpty = motivationText === ''
  const isMotivationSelectedOptionEmpty = motivationSelectedOption === undefined
  const isMotivationOpenAnswerSelected =
    motivationSelectedOption === MOTIVATION_VALUES.ANOTHER_MOTIVE
  const isCurrentStepExpectedSuccess = currentStep === SURVEY_STEPS.EXPECTED_SUCCESS
  const isExpectedSuccessTextEmpty = expectedSuccessText === ''
  const isExpectedSuccessOptionEmpty = expectedSuccesSelectedOption === undefined
  const isExpectSuccessOpenAnswerSelected =
    expectedSuccesSelectedOption === EXPECTED_SUCCESS_VALUES.ANOTHER_MOTIVE

  const isCurrentStepInitalExpectation = currentStep === SURVEY_STEPS.INITIAL_EXPECTATION
  const isInitialExpectationTextEmpty = initialExpectationText === ''

  const isDisabled =
    (isCurrentStepMotivation &&
      (isMotivationSelectedOptionEmpty ||
        (isMotivationOpenAnswerSelected && isMotivationTextEmpty))) ||
    (isCurrentStepExpectedSuccess &&
      (isExpectedSuccessOptionEmpty ||
        (isExpectSuccessOpenAnswerSelected && isExpectedSuccessTextEmpty))) ||
    (isCurrentStepInitalExpectation && isInitialExpectationTextEmpty)

  const steps = {
    [SURVEY_STEPS.MOTIVATION]: {
      component: MotivationStep,
      handleNextStep: () => {
        if (
          motivationSelectedOption !== MOTIVATION_VALUES.DID_NOT_HAVE_EXPECTED_SUCCESS &&
          motivationSelectedOption !== MOTIVATION_VALUES.DID_NOT_MEET_INITIAL_EXPECTATION
        ) {
          const answer1 = motivationSelectedOption
            ? `${MOTIVATION_ANSWER_DATAHUB_VALUES[motivationSelectedOption].label}${
                isMotivationOpenAnswerSelected ? ` ${motivationText}` : ''
              }`
            : ''

          const answer1Index = motivationSelectedOption
            ? MOTIVATION_ANSWER_DATAHUB_VALUES[motivationSelectedOption].index
            : undefined

          setProgressBarValue(CONCLUDED_PROGRESS_BAR_VALUE)
          handleDeleteCommunity({
            question1: MOTIVATION_QUESTION_DATAHUB_LABEL,
            question2: INITIAL_EXPECTATION_QUESTION_DATAHUB_LABEL,
            question3: EXPECTED_SUCESS_QUESTION_DATAHUB_LABEL,
            answer1,
            answer1Index
          })

          return
        }
        if (motivationSelectedOption === MOTIVATION_VALUES.DID_NOT_HAVE_EXPECTED_SUCCESS) {
          setCurrentStep(SURVEY_STEPS.EXPECTED_SUCCESS)
          setProgressBarValue(HALF_CONCLUDED_PROGRESS_BAR_VALUE)

          return
        }

        if (motivationSelectedOption === MOTIVATION_VALUES.DID_NOT_MEET_INITIAL_EXPECTATION) {
          setCurrentStep(SURVEY_STEPS.INITIAL_EXPECTATION)
          setProgressBarValue(HALF_CONCLUDED_PROGRESS_BAR_VALUE)

          return
        }
      }
    },
    [SURVEY_STEPS.EXPECTED_SUCCESS]: {
      component: ExpectedSuccessStep,
      handleNextStep: () => {
        const answer1 = motivationSelectedOption
          ? MOTIVATION_ANSWER_DATAHUB_VALUES[motivationSelectedOption].label
          : ''

        const answer1Index = motivationSelectedOption
          ? MOTIVATION_ANSWER_DATAHUB_VALUES[motivationSelectedOption].index
          : undefined

        const answer3 = expectedSuccesSelectedOption
          ? `${EXPECTED_SUCCESS_ANSWER_DATAHUB_VALUES[expectedSuccesSelectedOption].label}${
              isExpectSuccessOpenAnswerSelected ? ` ${expectedSuccessText}` : ''
            }`
          : ''

        const answer3Index = expectedSuccesSelectedOption
          ? EXPECTED_SUCCESS_ANSWER_DATAHUB_VALUES[expectedSuccesSelectedOption].index
          : undefined

        setProgressBarValue(CONCLUDED_PROGRESS_BAR_VALUE)
        handleDeleteCommunity({
          question1: MOTIVATION_QUESTION_DATAHUB_LABEL,
          question2: INITIAL_EXPECTATION_QUESTION_DATAHUB_LABEL,
          question3: EXPECTED_SUCESS_QUESTION_DATAHUB_LABEL,
          answer1,
          answer1Index,
          answer3,
          answer3Index
        })
      },
      handleBackStep: () => {
        setCurrentStep(SURVEY_STEPS.MOTIVATION)
        setProgressBarValue(INITIAL_PROGRESS_BAR_VALUE)
        setExpectedSuccesSelectedOption(undefined)
      }
    },
    [SURVEY_STEPS.INITIAL_EXPECTATION]: {
      component: InitialExpectationStep,
      handleNextStep: () => {
        const answer1 = motivationSelectedOption
          ? MOTIVATION_ANSWER_DATAHUB_VALUES[motivationSelectedOption].label
          : ''

        const answer1Index = motivationSelectedOption
          ? MOTIVATION_ANSWER_DATAHUB_VALUES[motivationSelectedOption].index
          : undefined

        setProgressBarValue(CONCLUDED_PROGRESS_BAR_VALUE)
        handleDeleteCommunity({
          question1: MOTIVATION_QUESTION_DATAHUB_LABEL,
          question2: INITIAL_EXPECTATION_QUESTION_DATAHUB_LABEL,
          question3: EXPECTED_SUCESS_QUESTION_DATAHUB_LABEL,
          answer1,
          answer1Index,
          answer2: initialExpectationText
        })
      },
      handleBackStep: () => {
        setCurrentStep(SURVEY_STEPS.MOTIVATION)
        setProgressBarValue(INITIAL_PROGRESS_BAR_VALUE)
        setInitialExpectationText('')
      }
    }
  }

  useEffect(() => {
    const handler = (event) => {
      const payload = (event as CustomEvent<ToggleDeleteModalEvent>).detail
      if (!payload.action && payload.communityId === undefined) {
        return
      }

      switch (payload.action) {
        case 'close':
          setIsOpen(false)
          break
        case 'open':
          setIsOpen(true)
          break
      }

      setCommunityId(payload.communityId)
      setCommunityName(payload.communityName)
    }

    toggleDeleteModalEventListener(handler)

    return () => {
      window.removeEventListener('delete_modal', handler)
    }
  }, [])

  const handleDelete = (communityId: number, communityName: string) => {
    toggleDeleteModalEventDispatch({ action: 'open', communityId, communityName })
  }

  return (
    <DeleteCommunityModalContext.Provider
      value={{
        steps,
        isOpen,
        isDisabled,
        currentStep,
        handleClose,
        handleDelete,
        motivationText,
        setCurrentStep,
        progressBarValue,
        setMotivationText,
        expectedSuccessText,
        setProgressBarValue,
        initialExpectationText,
        setExpectedSuccessText,
        motivationSelectedOption,
        setInitialExpectationText,
        setMotivationSelectedOption,
        expectedSuccesSelectedOption,
        setExpectedSuccesSelectedOption,
        communityId: Number(communityId)
      }}>
      <DeleteCommunityModal isOpen={isOpen} closeModal={handleClose} />
      {children}
    </DeleteCommunityModalContext.Provider>
  )
}
